import React from 'react';

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
const t = (k, o) => i18n.t(k, o);

/**
 * Inline SVG illustration of Veitvet Sportsklubb's football fields:
 *  - 11er bane (large) split horizontally into A (top) and B (bottom)
 *  - 7er bane (smaller) next to it
 *
 * Pure SVG so it scales perfectly on every screen and stays sharp.
 */
export const FieldMap = () => {
  useTranslation();
  return (
    <div className="w-full" data-testid="field-map">
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 sm:p-8">
        <div className="aspect-[16/9] w-full max-w-4xl mx-auto">
          <svg
            viewBox="0 0 800 450"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full"
            role="img"
            aria-label={t('pg.FieldMap.bb3094')}
          >
            {/* Background subtle grid */}
            <defs>
              <pattern id="grass" patternUnits="userSpaceOnUse" width="20" height="20">
                <rect width="20" height="20" fill="#16a34a" />
                <rect x="0" y="0" width="20" height="20" fill="url(#grass-stripe)" />
              </pattern>
              <linearGradient id="grass-stripe" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(255,255,255,0.04)" />
                <stop offset="50%" stopColor="rgba(0,0,0,0.04)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.04)" />
              </linearGradient>
              <linearGradient id="field-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#16a34a" />
                <stop offset="100%" stopColor="#15803d" />
              </linearGradient>
            </defs>

            {/* ============ 11er bane (large, on the right) ============ */}
            {/* Outer field */}
            <rect
              x="280"
              y="40"
              width="480"
              height="370"
              fill="url(#field-gradient)"
              stroke="#ffffff"
              strokeWidth="3"
              rx="2"
            />
            {/* Stripes for that authentic mowed-grass look */}
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <rect
                key={i}
                x="280"
                y={40 + i * 62}
                width="480"
                height="31"
                fill={i % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
              />
            ))}

            {/* Mid-line dividing A and B */}
            <line
              x1="280"
              y1="225"
              x2="760"
              y2="225"
              stroke="#ffffff"
              strokeWidth="3"
              strokeDasharray="8 6"
            />
            {/* Center circle */}
            <circle cx="520" cy="225" r="48" fill="none" stroke="#ffffff" strokeWidth="2" />
            <circle cx="520" cy="225" r="3" fill="#ffffff" />

            {/* Penalty box top (A) */}
            <rect
              x="380"
              y="40"
              width="280"
              height="60"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2"
            />
            <rect
              x="445"
              y="40"
              width="150"
              height="22"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2"
            />

            {/* Penalty box bottom (B) */}
            <rect
              x="380"
              y="350"
              width="280"
              height="60"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2"
            />
            <rect
              x="445"
              y="388"
              width="150"
              height="22"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2"
            />

            {/* Labels for A and B */}
            <g>
              <rect x="500" y="115" width="80" height="32" rx="6" fill="rgba(0,0,0,0.55)" />
              <text
                x="540"
                y="137"
                textAnchor="middle"
                fontFamily="ui-sans-serif, system-ui"
                fontSize="22"
                fontWeight="700"
                fill="#ffffff"
              >
                11er A
              </text>
            </g>
            <g>
              <rect x="500" y="305" width="80" height="32" rx="6" fill="rgba(0,0,0,0.55)" />
              <text
                x="540"
                y="327"
                textAnchor="middle"
                fontFamily="ui-sans-serif, system-ui"
                fontSize="22"
                fontWeight="700"
                fill="#ffffff"
              >
                11er B
              </text>
            </g>

            {/* ============ 7er bane (smaller, on the left) ============ */}
            <rect
              x="40"
              y="160"
              width="200"
              height="200"
              fill="url(#field-gradient)"
              stroke="#ffffff"
              strokeWidth="3"
              rx="2"
            />
            {[0, 1, 2, 3].map((i) => (
              <rect
                key={`s-${i}`}
                x="40"
                y={160 + i * 50}
                width="200"
                height="25"
                fill={i % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
              />
            ))}
            {/* center line */}
            <line x1="40" y1="260" x2="240" y2="260" stroke="#ffffff" strokeWidth="2" />
            <circle cx="140" cy="260" r="28" fill="none" stroke="#ffffff" strokeWidth="2" />
            <circle cx="140" cy="260" r="2.5" fill="#ffffff" />
            {/* boxes */}
            <rect x="80" y="160" width="120" height="34" fill="none" stroke="#ffffff" strokeWidth="2" />
            <rect x="80" y="326" width="120" height="34" fill="none" stroke="#ffffff" strokeWidth="2" />
            {/* Label */}
            <g>
              <rect x="100" y="245" width="80" height="32" rx="6" fill="rgba(0,0,0,0.55)" />
              <text
                x="140"
                y="267"
                textAnchor="middle"
                fontFamily="ui-sans-serif, system-ui"
                fontSize="22"
                fontWeight="700"
                fill="#ffffff"
              >
                7er
              </text>
            </g>

            {/* ============ Klubbhus marker ============ */}
            <g>
              <rect x="700" y="10" width="80" height="22" rx="4" fill="#0f172a" />
              <text
                x="740"
                y="25"
                textAnchor="middle"
                fontFamily="ui-sans-serif, system-ui"
                fontSize="11"
                fontWeight="600"
                fill="#ffffff"
              >
                {t('pg.FieldMap.14417c')}
              </text>
            </g>
          </svg>
        </div>

        {/* Legend */}
        <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
          <div className="flex items-center gap-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <div className="w-10 h-10 rounded bg-green-700 border-2 border-white shadow flex-shrink-0" />
            <div>
              <p className="font-bold text-slate-900 dark:text-white">11er A</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('pg.FieldMap.01ef4b')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <div className="w-10 h-10 rounded bg-green-700 border-2 border-white shadow flex-shrink-0" />
            <div>
              <p className="font-bold text-slate-900 dark:text-white">11er B</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('pg.FieldMap.1153ae')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <div className="w-10 h-10 rounded bg-green-700 border-2 border-white shadow flex-shrink-0" />
            <div>
              <p className="font-bold text-slate-900 dark:text-white">7er</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('pg.FieldMap.5d6384')}</p>
            </div>
          </div>
        </div>

        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400 text-center italic">
          {t('pg.FieldMap.206786')}
        </p>
      </div>
    </div>
  );
};

export default FieldMap;
